import type { PlatformAdapters } from '@hoedokloop/platform-ports';
import { StrictMode, useEffect, useRef, useState } from 'react';

import { App } from './app';
import { AppErrorBoundary } from './error-boundary';
import { RecoveryOnlyApp } from './recovery-only-app';
import { JuiceProvider } from './juice/juice-provider';
import { StudyProvider } from './features/study/study-context';
import { ThemeProvider } from './hooks/use-theme';
import { PlatformProvider } from './platform/platform-context';

export function HoedokLoopRoot({
  adapters,
  darkAllowed = true,
  recoveryProbe = true,
  onNormalReady,
}: {
  adapters: PlatformAdapters;
  darkAllowed?: boolean;
  recoveryProbe?: boolean;
  onNormalReady?: () => void;
}) {
  const [mode, setMode] = useState<
    'checking' | 'normal' | 'recovery' | 'offline-readonly' | 'unavailable'
  >(recoveryProbe ? 'checking' : 'normal');
  const entrySent = useRef(false);
  const checkMode = async () => {
    try {
      const response = await fetch('/healthz', {
        cache: 'no-store',
        signal: AbortSignal.timeout(5_000),
      });
      if (!response.ok) throw new Error('Recovery mode check failed');
      const recovery = response.headers.get('x-hoedokloop-recovery-only') === 'true';
      sessionStorage.setItem('hoedokloop:known-mode', recovery ? 'recovery' : 'normal');
      setMode(recovery ? 'recovery' : 'normal');
    } catch {
      const knownMode = sessionStorage.getItem('hoedokloop:known-mode');
      if (knownMode === 'recovery') setMode('recovery');
      else if (!navigator.onLine) setMode(knownMode === 'normal' ? 'normal' : 'offline-readonly');
      else setMode('unavailable');
    }
  };
  useEffect(() => {
    if (recoveryProbe) void checkMode();
  }, [recoveryProbe]);
  useEffect(() => {
    if (mode === 'normal' && !entrySent.current) {
      entrySent.current = true;
      onNormalReady?.();
    }
  }, [mode, onNormalReady]);
  if (mode === 'checking') return <p role="status">기록을 여는 중이에요.</p>;
  if (mode === 'unavailable')
    return (
      <div className="pn-page">
        <h1>연결을 확인하지 못했어요</h1>
        <p>기록을 보존하고 있어요. 연결을 확인한 뒤 다시 시도해 주세요.</p>
        <button onClick={() => void checkMode()}>다시 확인</button>
      </div>
    );
  if (mode === 'recovery' || mode === 'offline-readonly')
    return <RecoveryOnlyApp offlineUnverified={mode === 'offline-readonly'} />;
  return (
    <StrictMode>
      <AppErrorBoundary>
        <PlatformProvider adapters={adapters}>
          <ThemeProvider darkAllowed={darkAllowed}>
            <JuiceProvider>
              <StudyProvider>
                <App />
              </StudyProvider>
            </JuiceProvider>
          </ThemeProvider>
        </PlatformProvider>
      </AppErrorBoundary>
    </StrictMode>
  );
}
